import { useState, useMemo } from 'react';
import { Search, ArrowUpDown } from 'lucide-react';
import { useInventory } from '../hooks/useData';
import { getStatusColor } from '../utils/api';

export default function Inventory({ onSelectSku }) {
  const { data: inventory, isLoading, error } = useInventory();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortField, setSortField] = useState('urgency');
  const [sortDir, setSortDir] = useState('asc');

  const categories = useMemo(() => {
    if (!inventory) return [];
    return [...new Set(inventory.map(i => i.category))].sort();
  }, [inventory]);

  const filteredInventory = useMemo(() => {
    if (!inventory) return [];

    return inventory
      .filter(item => {
        const matchesSearch =
          item.sku.toLowerCase().includes(search.toLowerCase()) ||
          item.product_name.toLowerCase().includes(search.toLowerCase()) ||
          item.brand.toLowerCase().includes(search.toLowerCase());

        const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
        const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;

        return matchesSearch && matchesStatus && matchesCategory;
      })
      .sort((a, b) => {
        let aVal = a[sortField];
        let bVal = b[sortField];

        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        if (sortDir === 'asc') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });
  }, [inventory, search, statusFilter, categoryFilter, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-700 p-4 rounded-lg">
        Error loading inventory: {error.message}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Inventory</h1>
        <p className="text-gray-500">Stock levels and reorder status</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search SKU, product, or brand..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="CRITICAL">Critical</option>
            <option value="ORDER_NOW">Order Now</option>
            <option value="WATCH">Watch</option>
            <option value="HEALTHY">Healthy</option>
            <option value="OVERSTOCK">Overstock</option>
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {['CRITICAL', 'ORDER_NOW', 'WATCH', 'HEALTHY', 'OVERSTOCK'].map(status => {
          const count = inventory?.filter(i => i.status === status).length || 0;
          return (
            <button
              key={status}
              onClick={() => setStatusFilter(statusFilter === status ? 'all' : status)}
              className={`p-4 rounded-lg border-2 transition-colors ${
                statusFilter === status
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-100 bg-white hover:border-gray-200'
              }`}
            >
              <span className={getStatusColor(status)}>{status.replace('_', ' ')}</span>
              <p className="text-2xl font-bold text-gray-900 mt-2">{count}</p>
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th
                  className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('sku')}
                >
                  <div className="flex items-center gap-1">
                    SKU / Product
                    <ArrowUpDown size={14} />
                  </div>
                </th>
                <th
                  className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('category')}
                >
                  <div className="flex items-center gap-1">
                    Category
                    <ArrowUpDown size={14} />
                  </div>
                </th>
                <th
                  className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('current_stock')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Stock
                    <ArrowUpDown size={14} />
                  </div>
                </th>
                <th
                  className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('avg_weekly_demand')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Weekly Demand
                    <ArrowUpDown size={14} />
                  </div>
                </th>
                <th
                  className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('days_of_stock')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Days Left
                    <ArrowUpDown size={14} />
                  </div>
                </th>
                <th
                  className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('reorder_point')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Reorder Point
                    <ArrowUpDown size={14} />
                  </div>
                </th>
                <th
                  className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('suggested_order_qty')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Order Qty
                    <ArrowUpDown size={14} />
                  </div>
                </th>
                <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredInventory.map((item) => (
                <tr
                  key={item.sku}
                  className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer"
                  onClick={() => onSelectSku && onSelectSku(item.sku)}
                >
                  <td className="py-3 px-4">
                    <p className="font-medium text-gray-900">{item.sku}</p>
                    <p className="text-xs text-gray-500">{item.brand}</p>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600">{item.category}</td>
                  <td className="py-3 px-4 text-right">
                    <span className={`font-medium ${item.current_stock < item.reorder_point ? 'text-red-600' : 'text-gray-900'}`}>
                      {item.current_stock}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right text-gray-600">
                    {item.avg_weekly_demand}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span className={`font-medium ${item.days_of_stock < 14 ? 'text-red-600' : item.days_of_stock > 60 ? 'text-purple-600' : 'text-gray-900'}`}>
                      {item.days_of_stock}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right text-gray-600">
                    {item.reorder_point}
                  </td>
                  <td className="py-3 px-4 text-right">
                    {item.suggested_order_qty > 0 ? (
                      <span className="font-medium text-blue-600">{item.suggested_order_qty}</span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className={getStatusColor(item.status)}>
                      {item.status?.replace('_', ' ')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 text-sm text-gray-500">
          Showing {filteredInventory.length} of {inventory?.length || 0} items
        </div>
      </div>
    </div>
  );
}
